import React, {ReactElement, useState, useCallback, useMemo} from 'react';
import type {MenuListProps, GroupBase} from 'react-select';

const OPTION_HEIGHT = 35;
const BUFFER_SIZE = 5; // Extra items to render above/below viewport
const VIRTUALIZATION_THRESHOLD = 100; // Only virtualize lists with more than this many items

/**
 * Simple virtualized MenuList component for react-select.
 * Uses basic scroll-based virtualization instead of react-window
 * for better compatibility with react-select's menu portal.
 */
function VirtualizedMenuList<
    Option,
    IsMulti extends boolean = false,
    Group extends GroupBase<Option> = GroupBase<Option>
>(props: MenuListProps<Option, IsMulti, Group>) {
    const {children, maxHeight, innerRef} = props;

    // React Compiler wraps ref values in a Proxy for render-time access
    // tracking. react-select's class component stores the DOM element via
    // `innerRef` and later calls `scrollTo` / `scrollIntoView` on it.
    // When the call goes through a Proxy, `this` isn't a real Element and
    // the browser throws "Illegal invocation". Pre-binding `scrollTo` on
    // the raw DOM node before passing it to react-select ensures the native
    // method always has the correct `this`.
    const handleRef = useCallback((node: HTMLDivElement | null) => {
        if (node && typeof node.scrollTo === 'function') {
            const bound = node.scrollTo.bind(node);
            Object.defineProperty(node, 'scrollTo', {
                value: bound,
                writable: true,
                configurable: true,
            });
        }
        if (typeof innerRef === 'function') {
            innerRef(node);
        } else if (innerRef && typeof innerRef === 'object') {
            // eslint-disable-next-line react-hooks/immutability, react-compiler/react-compiler -- innerRef is a MutableRefObject; setting .current is react-select's documented contract (MenuListProps.innerRef). react-compiler skip acknowledged — the mutation is unavoidable here.
            (innerRef as React.MutableRefObject<HTMLDivElement | null>).current = node;
        }
    }, [innerRef]);

    // IMPORTANT: All hooks must be called before any conditional returns
    const [scrollTop, setScrollTop] = useState(0);

    const handleScroll = useCallback((e: React.UIEvent<HTMLDivElement>) => {
        setScrollTop(e.currentTarget.scrollTop);
    }, []);

    // Convert children to array for indexing
    const childrenArray = useMemo(
        () => React.Children.toArray(children) as ReactElement[],
        [children]
    );

    // Determine if we should virtualize
    const shouldVirtualize = childrenArray.length >= VIRTUALIZATION_THRESHOLD;

    // Calculate virtualization params (always calculate to avoid conditional hook behavior)
    const totalHeight = childrenArray.length * OPTION_HEIGHT;
    const visibleHeight = Math.min(maxHeight, totalHeight);
    const startIndex = Math.max(0, Math.floor(scrollTop / OPTION_HEIGHT) - BUFFER_SIZE);
    const visibleCount = Math.ceil(visibleHeight / OPTION_HEIGHT) + BUFFER_SIZE * 2;
    const endIndex = Math.min(childrenArray.length, startIndex + visibleCount);

    // Build visible items array (only used when virtualizing)
    const visibleItems = useMemo(() => {
        if (!shouldVirtualize) return null;

        const items = [];
        for (let i = startIndex; i < endIndex; i++) {
            items.push(
                <div
                    key={i}
                    style={{
                        position: 'absolute',
                        top: i * OPTION_HEIGHT,
                        left: 0,
                        right: 0,
                        height: OPTION_HEIGHT,
                    }}
                >
                    {childrenArray[i]}
                </div>
            );
        }
        return items;
    }, [shouldVirtualize, startIndex, endIndex, childrenArray]);

    // For small lists, render normally without virtualization
    if (!shouldVirtualize) {
        return (
            <div
                ref={handleRef}
                className="react-select__menu-list"
                role="listbox"
                style={{maxHeight, overflowY: 'auto'}}
            >
                {children}
            </div>
        );
    }

    // Render virtualized list
    return (
        <div
            ref={handleRef}
            className="react-select__menu-list"
            role="listbox"
            aria-multiselectable={false}
            style={{
                maxHeight,
                overflowY: 'auto',
                position: 'relative',
            }}
            onScroll={handleScroll}
        >
            {/* Spacer div to create correct scroll height */}
            <div style={{height: totalHeight, position: 'relative'}}>
                {visibleItems}
            </div>
        </div>
    );
}

export default VirtualizedMenuList;
